import { useEffect, useState, useCallback } from 'react';
import useServerApi from './useServerApi';


interface Company {
  id: number;
  name: string;
  count_calls: string;
  missed_calls: string;
  business_do: string;
  target_customers: string;
  most_common_customers: string;
  customer_service_priorities: string;
  additional_info: string;
}

export interface UserData {
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_verified: boolean;
  date_joined: string;
  is_agent_created: boolean;
  phone_number: string;
  website: string | null;
  language: number;
  twilio_sid: string;
  api_key: string;
  key: string;
  time_zone: string | null;
  finished_stepper: boolean;
  is_allow_to_stepper: boolean;
  has_subscription: boolean;
  variant_create_agent: string | null;
  agent_price_id: string | null;
  username: string | null;
  call_service: number;
  customer: string;
  billing_details_last_changed: string;
  last_synced_with_stripe: string | null;
  bill: any;
  company: Company;
  subscription_info: any;
  minutes_info: number;
  agent_info: AgentInfo | null;
  user_info: UserInfo | null;
  db_subscription_information?: DbSubscriptionInformation;
  has_premium_subscription: boolean;
}

export interface DbSubscriptionInformation {
  status: string;
  current_period_start: string;
  trial_period_end: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  stripe_subscription_id: string;
  price: number;
  interval: string;
  has_access: boolean;
}

export interface UserInfo {
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_verified: boolean;
  date_joined: string;
  is_agent_created: boolean;
  phone_number: string;
  website: string | null;
  language: number;
  twilio_sid: string;
  api_key: string;
  key: string;
  time_zone: string | null;
  finished_stepper: boolean;
  is_allow_to_stepper: boolean;
  has_subscription: boolean;
  variant_create_agent: string | null;
  agent_price_id: string | null;
  username: string | null;
  call_service: number;
  customer: string;
  billing_details_last_changed: string;
  last_synced_with_stripe: string | null;
  company: Company;
}
export interface AgentInfo {
  id: number;
  uuid: string;
  phone_number: string;
  title: string;
  call_service: number;
  is_active: boolean;
  status: string;
  time_zone: string;
  subscription_price_id: string | null;
  number_of_additional_minutes_purchased: number;
  is_delete: boolean;
  day_when_delete: string | null;
  prepend_quality_message: boolean;
  voice_name: string | null;
  voice_id: string | null;
}

function useGetUserData(isAuth: boolean): [UserData | null, boolean, () => void] {
  const [{ get }] = useServerApi();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasPremiumSubscription = (userData: UserData | null): boolean => {
    return userData?.db_subscription_information != null && userData.db_subscription_information.price >= 99;
  };

  const fetchUserData = useCallback(async () => {
    if (!isAuth) {
      setIsLoading(false);
      setUserData(null);
      return;
    }
    setIsLoading(true);
    try {
      const [success, response] = await get('/rest_endpoints/subscriptions/checkUserData');
      if (success) {
        const ud = response;
        ud.has_premium_subscription = hasPremiumSubscription(ud);
        setUserData(ud);
      } else {
        setUserData(null);
        console.error('Failed to fetch user data:', response);
      }
    } catch (error) {
      setUserData(null);
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuth, get]);

  useEffect(() => {
    fetchUserData();
  }, [isAuth, fetchUserData]);

  return [userData, isLoading, fetchUserData];
}

export default useGetUserData;
